import LegalPage from "@/components/LegalPage";
import { Link } from "react-router-dom";

/**
 * /terms — Allgemeine Geschäftsbedingungen (AGB) für PineForge.AI.
 *
 * ⚠️ NON-PUBLIC NOTICE:
 * Legal pages contain placeholders and must not be considered final until
 * operator data is inserted and reviewed.
 *
 * TODO: Review consumer-facing VAT display requirements before public paid
 * checkout is enabled.
 * TODO: Legal review required before enabling paid checkout / Stripe.
 */
export default function Terms() {
  return (
    <LegalPage
      title="Allgemeine Geschäftsbedingungen (AGB)"
      subtitle="Diese Bedingungen regeln die Nutzung des Dienstes PineForge.AI."
      updatedAt="05.07.2026"
    >
      <Section n="1" title="Anbieter und Geltungsbereich">
        <p>
          Anbieter des Dienstes <strong>PineForge.AI</strong> (nachfolgend
          „Dienst“ oder „PineForge.AI“) ist:
        </p>
        <address className="not-italic block bg-[#12141C] border border-[#1E212B] rounded-lg p-4 my-3 text-zinc-200 text-sm">
          Gökhan Tan<br />
          Einzelunternehmen<br />
          Jägerhofallee 34<br />
          71638 Ludwigsburg<br />
          Deutschland<br />
          E-Mail: pineforgeai@gmx.de<br />
          Support: customerservice@pineforge.ai<br />
          Telefon: +49-160-4526226
        </address>
        <p>
          Diese Allgemeinen Geschäftsbedingungen (nachfolgend „AGB“) gelten
          für alle Verträge zwischen dem Anbieter und dem Nutzer über die
          Nutzung von PineForge.AI, einschließlich der kostenlosen und der
          zukünftig kostenpflichtigen Nutzung. Abweichende Bedingungen des
          Nutzers werden nicht anerkannt, es sei denn, der Anbieter stimmt
          ihrer Geltung ausdrücklich schriftlich zu.
        </p>
      </Section>

      <Section n="2" title="Gegenstand des Dienstes">
        <p>
          PineForge.AI ist ein Softwarewerkzeug, das Nutzern hilft, Entwürfe
          von Pine-Script-Code für die TradingView-Plattform zu erstellen, zu
          überarbeiten, zu erklären, zu reparieren und zu exportieren. Der
          Dienst nutzt KI-gestützte Modelle, um auf Grundlage der vom Nutzer
          eingegebenen Eingaben (Prompts) Code-Vorschläge zu erzeugen.
        </p>
        <p>
          PineForge.AI ist ein reines Software-Werkzeug. Der Dienst stellt
          <strong> keine Finanz-, Anlage- oder Trading-Beratung </strong>
          dar (siehe Ziffer 11).
        </p>
      </Section>

      <Section n="3" title="Registrierung und Nutzerkonto">
        <p>
          Für die Nutzung des Dienstes ist ein kostenloses Nutzerkonto
          erforderlich. Der Nutzer verpflichtet sich, bei der Registrierung
          wahrheitsgemäße Angaben zu machen und diese aktuell zu halten. Die
          Zugangsdaten sind vertraulich zu behandeln und dürfen nicht an
          Dritte weitergegeben werden.
        </p>
        <p>
          Der Anbieter behält sich vor, Nutzerkonten bei Verstößen gegen
          diese AGB, insbesondere gegen die Regeln zur untersagten Nutzung
          (Ziffer 13), zu sperren oder zu löschen.
        </p>
      </Section>

      <Section n="4" title="Kostenlose Credits">
        <p>
          Nach erfolgreicher Registrierung erhält der Nutzer ein Guthaben in
          Form von kostenlosen Credits (aktuell 10 Credits), das für die
          Nutzung der kostenpflichtigen Funktionen des Dienstes eingesetzt
          werden kann.
        </p>
        <p>
          Kostenlose Credits werden einmalig bei Registrierung gewährt und
          nicht automatisch monatlich erneuert. Die Anzahl der bereit-
          gestellten kostenlosen Credits kann vom Anbieter jederzeit
          angepasst werden. Ein Rechtsanspruch auf kostenlose Credits
          besteht nicht.
        </p>
      </Section>

      <Section n="5" title="Kostenpflichtige Pläne und Credits">
        <p>
          Der Anbieter kann kostenpflichtige Abonnement-Pläne anbieten
          („Starter“, „Pro“), die dem Nutzer ein festes Credit-Kontingent pro
          Credit-Zeitraum bereitstellen. Details zu den einzelnen Plänen sind
          auf der{" "}
          <Link to="/pricing" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
            Preisübersicht
          </Link>{" "}
          einsehbar.
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            <strong>Starter:</strong> 100 Credits pro Credit-Zeitraum.
          </li>
          <li>
            <strong>Pro:</strong> 500 Credits pro Credit-Zeitraum.
          </li>
          <li>
            Jahresabonnements werden jährlich abgerechnet, die zugehörigen
            Abo-Credits werden dennoch monatlich gemäß dem gewählten Tarif
            erneuert.
          </li>
          <li>
            Nicht genutzte Abo-Credits werden nicht in den nächsten Credit-
            Zeitraum übertragen (kein Rollover).
          </li>
        </ul>
        <p>
          <strong>Credit-Erneuerung und Verfall:</strong> Abo-Credits werden
          zu Beginn des jeweiligen Credit-Zeitraums gemäß dem gewählten Tarif
          erneuert und nicht in den nächsten Zeitraum übertragen. Nicht
          genutzte Abo-Credits verfallen am Ende des jeweiligen Credit-
          Zeitraums. Bei Jahresabonnements erfolgt die Abrechnung jährlich;
          die zugehörigen Abo-Credits werden dennoch monatlich gemäß dem
          jeweiligen Tarif erneuert.
        </p>
        <p>
          Credits werden nach der jeweils in der App angezeigten Credit-Logik
          verbraucht. Zum Zeitpunkt dieser AGB-Version gilt insbesondere:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            Reine Erklärungen zu bestehendem Skript-Code können nach aktueller
            Produktpolitik kostenfrei sein.
          </li>
          <li>
            Code-verändernde Generierungen und Reparaturen können nach
            aktueller Produktpolitik Credits verbrauchen.
          </li>
          <li>
            Fehlgeschlagene Generierungen oder Reparaturen sollen keine
            Credits verbrauchen, soweit der Dienst dies technisch erkennt.
          </li>
        </ul>
        <p className="text-zinc-400 text-xs">
          Die konkrete Anzahl verbrauchter Credits wird dem Nutzer im
          jeweiligen Kontext im Dienst angezeigt. Der Credit-Verbrauch pro
          Aktion richtet sich nach der Komplexität der jeweiligen
          AI-Aktion: einfache Chat-Erklärungen verbrauchen 1 Credit,
          Code-Generierungen und Code-Änderungen können 2–5 Credits
          verbrauchen, je nach Umfang und Komplexität. Fehlgeschlagene
          AI-Aktionen (Timeout, fehlgeschlagene Qualitätsprüfung, kein
          verwertbares Ergebnis) werden automatisch dem Kredit-Konto
          gutgeschrieben.
        </p>
      </Section>

      <Section n="6" title="Vertragsschluss">
        <p>
          Durch das Anlegen eines Nutzerkontos kommt ein unentgeltlicher
          Nutzungsvertrag über die kostenlose Nutzung des Dienstes zustande.
        </p>
        <p>
          Für kostenpflichtige Abonnements: Der Nutzer wählt einen Plan aus
          und schließt den Bestellvorgang über den vom Anbieter
          bereitgestellten Zahlungsprozess ab. Der Vertrag über das kosten-
          pflichtige Abonnement kommt zustande, sobald der Anbieter dem
          Nutzer den Zugang zum kostenpflichtigen Plan bestätigt.
        </p>
        <p className="text-zinc-400 text-xs">
          Hinweis: Zum aktuellen Zeitpunkt ist der bezahlpflichtige Checkout
          noch nicht aktiviert. Kostenpflichtige Abonnements werden erst über
          den Zahlungsdienstleister abgewickelt, sobald der Checkout
          freigeschaltet ist.
        </p>
      </Section>

      <Section n="7" title="Preise, Umsatzsteuer und Zahlungsabwicklung">
        <p>
          Alle angegebenen Preise sind Nettopreise. Umsatzsteuer bzw.
          anwendbare Steuern können abhängig vom Standort des Kunden im
          Checkout hinzukommen.
        </p>
        <p className="text-zinc-400 text-xs">
          {/* TODO: Review consumer-facing VAT display requirements before public paid checkout is enabled. */}
          Hinweis: Für Verbraucher können in bestimmten Rechtsordnungen
          Anforderungen an die Bruttopreis-Angabe gelten. Die konkrete
          Umsetzung wird vor Aktivierung des bezahlpflichtigen Checkouts
          geprüft.
        </p>
        <p>
          Die Zahlungsabwicklung soll nach Aktivierung über den Zahlungs-
          dienstleister <strong>Stripe</strong> erfolgen. Konkrete
          Zahlungsmethoden werden dem Nutzer im Checkout angezeigt. Zum
          aktuellen Zeitpunkt findet keine Zahlungsabwicklung statt und es
          werden keine Zahlungsdaten für kostenpflichtige Abonnements
          verarbeitet.
        </p>
      </Section>

      <Section n="8" title="Laufzeit, Verlängerung und Kündigung">
        <p>
          Kostenpflichtige Abonnements werden mit einer monatlichen oder
          jährlichen Laufzeit angeboten und verlängern sich – sofern nicht
          gekündigt – jeweils automatisch um die gewählte Laufzeit.
        </p>
        <p>
          Der Nutzer kann sein kostenpflichtiges Abonnement zum Ende der
          jeweils laufenden Vertragsperiode kündigen. Sobald der bezahlte
          Kundenbereich verfügbar ist, soll die Kündigung über den Account-
          bzw. Kundenportal-Bereich möglich sein. Alternativ genügt eine
          Kündigung in Textform an <strong>customerservice@pineforge.ai</strong>.
        </p>
        <p>
          Das Recht zur außerordentlichen Kündigung aus wichtigem Grund
          bleibt unberührt.
        </p>
      </Section>

      <Section n="9" title="Nutzung des Dienstes">
        <p>
          Der Nutzer ist verpflichtet, den Dienst nur im Rahmen der geltenden
          Gesetze und dieser AGB zu nutzen. Der Nutzer trägt die
          Verantwortung für die Inhalte, die er in den Dienst eingibt, sowie
          für die Verwendung der vom Dienst erzeugten Ausgaben.
        </p>
      </Section>

      <Section n="10" title="KI-generierte Inhalte und Verantwortung des Nutzers">
        <p>
          PineForge.AI erzeugt Code-Vorschläge auf Basis KI-gestützter
          Modelle. Diese Vorschläge können Fehler, Unvollständigkeiten oder
          nicht zur konkreten Absicht des Nutzers passende Ergebnisse
          enthalten.
        </p>
        <p>
          Der Nutzer ist selbst verantwortlich für:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            das manuelle Überprüfen, Testen und Validieren des generierten
            Codes in TradingView,
          </li>
          <li>
            die Entscheidung, ob und wie der Code eingesetzt wird,
          </li>
          <li>
            die Einhaltung aller anwendbaren Gesetze bei der Nutzung des
            generierten Codes.
          </li>
        </ul>
        <p>
          Es wird ausdrücklich <strong>keine Zusicherung</strong> gegeben,
          dass generierter Code fehlerfrei kompiliert oder für einen
          bestimmten Zweck geeignet ist.
        </p>
      </Section>

      <Section n="11" title="Kein Finanz-, Anlage- oder Trading-Beratung">
        <p>
          PineForge.AI ist <strong>ausdrücklich kein</strong>{" "}
          Finanzberatungs-, Anlageberatungs- oder Signal-Dienst. Der Dienst
          liefert keine Anlageempfehlungen, keine Handelssignale, keine
          Gewinngarantien und keine Trading-Garantien.
        </p>
        <p>
          Handel und Investitionen sind mit erheblichen Risiken verbunden
          und können zu Verlusten bis hin zum Totalverlust des eingesetzten
          Kapitals führen. Vergangene Wertentwicklungen sind keine Garantie
          für zukünftige Ergebnisse. Der Nutzer entscheidet eigenverantwortlich
          über den Einsatz jeglicher aus dem Dienst hervorgegangener Skripte
          im echten Handelsumfeld.
        </p>
      </Section>

      <Section n="12" title="TradingView / Pine Script Hinweis">
        <p>
          PineForge.AI ist ein unabhängiges Werkzeug und steht in keiner
          geschäftlichen Verbindung zu TradingView, Inc. TradingView® und
          Pine Script® sind Marken der jeweiligen Rechteinhaber.
        </p>
      </Section>

      <Section n="13" title="Verbotene Nutzung">
        <p>Untersagt ist insbesondere:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            der Einsatz des Dienstes für rechtswidrige Zwecke, insbesondere
            für Marktmanipulation, betrügerische Handlungen oder Verstöße
            gegen Kapitalmarktrecht,
          </li>
          <li>
            der Versuch, Sicherheitsvorkehrungen des Dienstes zu umgehen, den
            Dienst durch automatisiertes Massenverhalten zu belasten, oder
            unbefugten Zugriff auf fremde Nutzerkonten zu erlangen,
          </li>
          <li>
            das Weiterverkaufen des Dienstes oder von Credits ohne
            ausdrückliche Zustimmung des Anbieters,
          </li>
          <li>
            das Eingeben personenbezogener Daten Dritter ohne entsprechende
            Rechtsgrundlage,
          </li>
          <li>
            die Nutzung des Dienstes zur Erstellung schädlicher oder
            missbräuchlicher Inhalte.
          </li>
        </ul>
      </Section>

      <Section n="14" title="Verfügbarkeit und technische Änderungen">
        <p>
          Der Anbieter ist bestrebt, den Dienst mit hoher Verfügbarkeit
          bereitzustellen, garantiert jedoch keine unterbrechungsfreie
          Verfügbarkeit. Wartungsarbeiten, technische Störungen oder
          Änderungen an den zugrunde liegenden KI-Modellen können zu
          zeitweiligen Einschränkungen führen.
        </p>
        <p>
          Der Anbieter ist berechtigt, den Funktionsumfang des Dienstes
          weiterzuentwickeln, Funktionen anzupassen oder einzustellen, soweit
          dies für den Nutzer zumutbar ist.
        </p>
      </Section>

      <Section n="15" title="Rechte an eingegebenen Inhalten">
        <p>
          Der Nutzer behält sämtliche Rechte an den von ihm eingegebenen
          Inhalten (Prompts, eigenem Pine-Script-Code, Beschreibungen). Der
          Nutzer räumt dem Anbieter ein einfaches, nicht ausschließliches
          Nutzungsrecht ein, diese Inhalte im Rahmen der Erbringung des
          Dienstes technisch zu verarbeiten (insbesondere zur Weitergabe an
          die zugrunde liegenden KI-Modelle).
        </p>
      </Section>

      <Section n="16" title="Rechte an generierten Ausgaben">
        <p>
          Soweit rechtlich zulässig, kann der Nutzer die von PineForge.AI
          erzeugten Code-Ausgaben im Rahmen seiner Nutzung frei verwenden.
          Der Nutzer erkennt an, dass KI-generierte Inhalte urheberrechtlich
          eingeschränkt schutzfähig sein können und dass identische oder
          ähnliche Ausgaben anderen Nutzern angezeigt werden könnten. Der
          Anbieter garantiert keine Exklusivität einzelner Ausgaben.
        </p>
      </Section>

      <Section n="17" title="Gewährleistung">
        <p>
          Der Anbieter erbringt seine Leistungen fachgerecht. Für die
          KI-gestützte Code-Generierung wird jedoch keine Gewähr für
          Fehlerfreiheit, Kompilierbarkeit, Sicherheit oder Eignung für
          einen bestimmten Zweck übernommen. Die gesetzlichen Ansprüche
          eines Verbrauchers bleiben unberührt.
        </p>
      </Section>

      <Section n="18" title="Haftung">
        <p>
          Der Anbieter haftet unbeschränkt für Vorsatz und grobe
          Fahrlässigkeit sowie nach den Vorschriften des Produkthaftungs-
          gesetzes. Bei leichter Fahrlässigkeit haftet der Anbieter nur bei
          Verletzung wesentlicher Vertragspflichten (Kardinalpflichten) und
          nur begrenzt auf den bei Vertragsschluss vorhersehbaren, typischen
          Schaden.
        </p>
        <p>
          Eine Haftung für Handelsverluste, entgangene Gewinne oder mittelbare
          Schäden, die aus der Nutzung generierter Skripte im echten
          Handelsumfeld entstehen, ist – soweit gesetzlich zulässig – ausge-
          schlossen. Der Nutzer trägt die alleinige Verantwortung für seine
          Handelsentscheidungen.
        </p>
      </Section>

      <Section n="19" title="Widerrufsrecht für Verbraucher">
        <p>
          Verbrauchern steht ein gesetzliches Widerrufsrecht zu. Details
          hierzu und das{" "}
          <Link to="/cancellation" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
            Muster der Widerrufsbelehrung
          </Link>{" "}
          sowie das{" "}
          <Link to="/cancellation-form" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
            Muster-Widerrufsformular
          </Link>{" "}
          finden Sie auf den entsprechenden Seiten.
        </p>
        <p className="text-zinc-400 text-xs">
          Hinweis: Für digitale Inhalte und Dienstleistungen können nach
          Beginn der Ausführung besondere Regeln zum Erlöschen des
          Widerrufsrechts gelten. Ausdrückliche Zustimmung und Bestätigung
          werden im Checkout-Prozess entsprechend abgefragt.
        </p>
      </Section>

      <Section n="20" title="Datenschutzverweis">
        <p>
          Informationen zur Verarbeitung personenbezogener Daten finden sich
          in der{" "}
          <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">
            Datenschutzerklärung
          </Link>.
        </p>
      </Section>

      <Section n="21" title="Änderungen der AGB">
        <p>
          Der Anbieter ist berechtigt, diese AGB mit Wirkung für die Zukunft
          anzupassen, wenn dies aus rechtlichen, regulatorischen oder
          produktbezogenen Gründen erforderlich ist. Über wesentliche
          Änderungen wird der Nutzer rechtzeitig in geeigneter Form informiert.
          Widerspricht der Nutzer den Änderungen nicht innerhalb einer
          angemessenen Frist, gelten die geänderten AGB als angenommen.
        </p>
      </Section>

      <Section n="22" title="Schlussbestimmungen">
        <p>
          Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss
          des UN-Kaufrechts. Zwingende verbraucherschützende Bestimmungen des
          Rechts des Staates, in dem der Verbraucher seinen gewöhnlichen
          Aufenthalt hat, bleiben unberührt.
        </p>
        <p>
          Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, so
          bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
        </p>
      </Section>

      <Section n="23" title="Kontakt">
        <p>
          Fragen zu diesen AGB richten Sie bitte an: <strong>customerservice@pineforge.ai</strong>.
        </p>
      </Section>
    </LegalPage>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`}>
      <h2 className="text-lg font-semibold text-white mb-3 flex items-baseline gap-3">
        <span className="text-[11px] font-mono text-zinc-500 tabular-nums">§ {n}</span>
        <span>{title}</span>
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
